<script setup>
import { computed } from 'vue';
const props = defineProps({
  variant: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'primary', 'secondary', 'success', 'warning', 'error', 'info'].includes(value)
  },
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['small', 'medium', 'large'].includes(value)
  },
  rounded: {
    type: Boolean,
    default: false
  },
  outlined: {
    type: Boolean,
    default: false
  },
  closable: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close']);

const chipClasses = computed(() => [
  'ui-chip',
  `ui-chip--${props.variant}`,
  `ui-chip--${props.size}`,
  {
    'ui-chip--rounded': props.rounded,
    'ui-chip--outlined': props.outlined,
    'ui-chip--disabled': props.disabled,
    'ui-chip--closable': props.closable
  }
]);

const handleClose = () => {
  if (props.disabled) return;
  emit('close');
};
</script>

<template>
  <div :class="chipClasses">
    <!-- Start icon slot -->
    <span v-if="$slots.startIcon" class="ui-chip__start-icon">
      <slot name="startIcon" />
    </span>

    <!-- Content -->
    <span class="ui-chip__content">
      <slot />
    </span>

    <!-- Close button -->
    <button v-if="closable" class="ui-chip__close" @click="handleClose" :disabled="disabled" aria-label="Remove">
      <v-icon size="14">mdi-close</v-icon>
    </button>

    <!-- End icon slot -->
    <span v-if="$slots.endIcon" class="ui-chip__end-icon">
      <slot name="endIcon" />
    </span>
  </div>
</template>

<style scoped>
.ui-chip {
  /* Base styles matching the original .label class */
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 700;
  white-space: nowrap;
  vertical-align: middle;
  border-radius: 4px;
  transition: all 0.2s ease-in-out;

  /* Default variant - matches original .label */
  background-color: #e8e8e8;
  color: #191919;
  border: 1px solid transparent;
}

/* Size variants */
.ui-chip--small {
  padding: 2px 8px;
  font-size: 11px;
  line-height: 1.2;
}

.ui-chip--medium {
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.3;
}

.ui-chip--large {
  padding: 6px 16px;
  font-size: 14px;
  line-height: 1.4;
}

/* Rounded variant */
.ui-chip--rounded {
  border-radius: 50px;
}

/* Color variants */
.ui-chip--default {
  background-color: #e8e8e8;
  color: #191919;
}

.ui-chip--primary {
  background-color: #8c1d40;
  color: white;
}

.ui-chip--secondary {
  background-color: #f5f5f5;
  color: #333;
}

.ui-chip--success {
  background-color: #4caf50;
  color: white;
}

.ui-chip--warning {
  background-color: #ff9800;
  color: white;
}

.ui-chip--error {
  background-color: #f44336;
  color: white;
}

.ui-chip--info {
  background-color: #2196f3;
  color: white;
}

/* Outlined variants */
.ui-chip--outlined {
  background-color: transparent;
}

.ui-chip--outlined.ui-chip--default {
  border-color: #e8e8e8;
  color: #191919;
}

.ui-chip--outlined.ui-chip--primary {
  border-color: #8c1d40;
  color: #8c1d40;
}

.ui-chip--outlined.ui-chip--secondary {
  border-color: #f5f5f5;
  color: #333;
}

.ui-chip--outlined.ui-chip--success {
  border-color: #4caf50;
  color: #4caf50;
}

.ui-chip--outlined.ui-chip--warning {
  border-color: #ff9800;
  color: #ff9800;
}

.ui-chip--outlined.ui-chip--error {
  border-color: #f44336;
  color: #f44336;
}

.ui-chip--outlined.ui-chip--info {
  border-color: #2196f3;
  color: #2196f3;
}

/* Disabled state */
.ui-chip--disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Icon styles */
.ui-chip__start-icon,
.ui-chip__end-icon {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.ui-chip__start-icon {
  margin-left: -2px;
}

.ui-chip__end-icon {
  margin-right: -2px;
}

.ui-chip__content {
  display: inline-flex;
  align-items: center;
}

/* Close button */
.ui-chip__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  margin-left: 4px;
  margin-right: -2px;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s ease-in-out;
  color: inherit;
  opacity: 0.7;
}

.ui-chip__close:hover:not(:disabled) {
  opacity: 1;
  background-color: rgba(0, 0, 0, 0.1);
}

.ui-chip__close:disabled {
  cursor: not-allowed;
  opacity: 0.3;
}

/* Responsive adjustments for small screens */
@media (max-width: 600px) {
  .ui-chip--small {
    font-size: 10px;
  }

  .ui-chip--medium {
    font-size: 11px;
  }

  .ui-chip--large {
    font-size: 13px;
  }
}
</style>
